/**
 * Color utility functions for dynamic theme generation.
 * Derives a full MUI palette from primary + secondary hex colors.
 */

type RGB = { r: number; g: number; b: number };
type HSL = { h: number; s: number; l: number };

function hexToRgb(hex: string): RGB {
	const clean = hex.replace("#", "");
	return {
		r: parseInt(clean.substring(0, 2), 16),
		g: parseInt(clean.substring(2, 4), 16),
		b: parseInt(clean.substring(4, 6), 16),
	};
}

function rgbToHex({ r, g, b }: RGB): string {
	const toHex = (n: number) =>
		Math.max(0, Math.min(255, Math.round(n)))
			.toString(16)
			.padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl({ r, g, b }: RGB): HSL {
	const rN = r / 255;
	const gN = g / 255;
	const bN = b / 255;
	const max = Math.max(rN, gN, bN);
	const min = Math.min(rN, gN, bN);
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case rN:
				h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
				break;
			case gN:
				h = ((bN - rN) / d + 2) / 6;
				break;
			case bN:
				h = ((rN - gN) / d + 4) / 6;
				break;
		}
	}

	return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb({ h, s, l }: HSL): RGB {
	const sN = s / 100;
	const lN = l / 100;
	const hN = h / 360;

	if (sN === 0) {
		const v = Math.round(lN * 255);
		return { r: v, g: v, b: v };
	}

	const hue2rgb = (p: number, q: number, t: number) => {
		let tN = t;
		if (tN < 0) tN += 1;
		if (tN > 1) tN -= 1;
		if (tN < 1 / 6) return p + (q - p) * 6 * tN;
		if (tN < 1 / 2) return q;
		if (tN < 2 / 3) return p + (q - p) * (2 / 3 - tN) * 6;
		return p;
	};

	const q = lN < 0.5 ? lN * (1 + sN) : lN + sN - lN * sN;
	const p = 2 * lN - q;

	return {
		r: Math.round(hue2rgb(p, q, hN + 1 / 3) * 255),
		g: Math.round(hue2rgb(p, q, hN) * 255),
		b: Math.round(hue2rgb(p, q, hN - 1 / 3) * 255),
	};
}

export function lighten(hex: string, amount: number): string {
	const hsl = rgbToHsl(hexToRgb(hex));
	hsl.l = Math.min(100, hsl.l + amount);
	return rgbToHex(hslToRgb(hsl));
}

export function darken(hex: string, amount: number): string {
	const hsl = rgbToHsl(hexToRgb(hex));
	hsl.l = Math.max(0, hsl.l - amount);
	return rgbToHex(hslToRgb(hsl));
}

/**
 * Returns relative luminance of a color (0-1).
 * Used for WCAG contrast calculations.
 */
function getLuminance(hex: string): number {
	const { r, g, b } = hexToRgb(hex);
	const [rL, gL, bL] = [r, g, b].map((c) => {
		const s = c / 255;
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
	});
	return 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
}

/**
 * Returns black or white text color for best contrast (WCAG AA).
 */
export function getContrastText(hex: string): string {
	return getLuminance(hex) > 0.179 ? "#000000" : "#FFFFFF";
}

/**
 * Documented global default for the `tertiary` palette role (JANUS lilac,
 * `--brand-tertiary-main`). Used when a tenant provides no `tertiaryColor`
 * (SIMS-7505 AC2) — a distinct constant, NOT derived from primary/secondary.
 */
export const DEFAULT_TERTIARY_COLOR = "#7034E7";

/**
 * Documented global default for the `surfaceTint` palette role (JANUS
 * `--surface-tint` / `--janus-blue-100`). Used when a tenant provides no
 * `surfaceTint` (SIMS-7608 AC2) — a distinct role, NOT `surfaceLight`/`#FFFFFF`
 * or a derived value. Per-tenant values (SIMS #E9D0F4, …) are backfilled in SIMS-7513.
 */
export const DEFAULT_SURFACE_TINT = "#DFF5FF";

export interface ThemeColorsConfig {
	primaryColor: string;
	secondaryColor: string;
	surfaceColor?: string;
	surfaceLight?: string;
	surfaceDark?: string;
	primaryDark?: string;
	secondaryLight?: string;
	outlineMain?: string;
	outlineDark?: string;
	textMainDark2?: string;
	textMainMain2?: string;
	textSecondaryMain?: string;
	tertiaryColor?: string;
	surfaceTint?: string;
	fontFamily?: string;
}

/**
 * Generates the full themePalette structure from theme color configuration.
 *
 * Based on the B2D brand spec:
 *  - primaryColor (#276048) -> navigation, menu, buttons, key highlights, accent
 *  - secondaryColor (#70A815) -> secondary actions, links, badges
 *  - surfaceColor (#DEE7E7) -> backgrounds, cards, surfaces
 *  - fontFamily (Roboto) -> font across the UI (weights: Regular, Medium, Semibold, Bold)
 */
export function generatePalette(config: ThemeColorsConfig) {
	const { primaryColor, secondaryColor, surfaceColor } = config;

	// Tertiary is a distinct role (JANUS lilac accent), NOT derived from
	// primary/secondary; falls back to the documented default (SIMS-7505 AC2).
	const tertiaryColor = config.tertiaryColor || DEFAULT_TERTIARY_COLOR;

	// surfaceTint is a distinct tinted-surface role (JANUS blue-100), NOT
	// surfaceLight/#FFFFFF or derived; falls back to the documented default (SIMS-7608 AC2).
	const surfaceTintColor = config.surfaceTint || DEFAULT_SURFACE_TINT;

	const primaryDark = config.primaryDark || darken(primaryColor, 12);
	// primaryLight is used for inactive menu text/icons on the dark sidebar.
	// Derive a desaturated, high-lightness tint from the primary hue.
	// This matches the original purple behavior (#BAA3CA: h=275, s=27, l=72).
	const primaryHsl = rgbToHsl(hexToRgb(primaryColor));
	const primaryLight = rgbToHex(
		hslToRgb({ h: primaryHsl.h, s: Math.min(primaryHsl.s, 25), l: 72 }),
	);
	const primaryMain = primaryColor;

	const secondaryDark = darken(secondaryColor, 20);
	const secondaryLight = config.secondaryLight || lighten(secondaryColor, 30);

	// Surface: use explicit surfaceColor if provided, otherwise derive from primary
	const surfaceMain = surfaceColor || lighten(primaryColor, 42);
	const surfaceDark =
		config.surfaceDark ||
		(surfaceColor ? darken(surfaceColor, 8) : lighten(primaryColor, 35));
	const surfaceLight = config.surfaceLight || "#FFFFFF";

	// Outline colors derived from surface (overridable)
	const outlineMain =
		config.outlineMain || (surfaceColor ? darken(surfaceColor, 12) : "#D9D1DD");
	const outlineLight = surfaceColor ? lighten(surfaceColor, 5) : "#E4DEE9";
	const outlineDark =
		config.outlineDark || (surfaceColor ? darken(surfaceColor, 25) : "#ADA6B3");

	// Icon colors: neutral with primary accent
	const iconMain = surfaceColor ? darken(surfaceColor, 30) : "#ADA6B3";
	const iconLight = surfaceColor ? darken(surfaceColor, 15) : "#D9D1DD";

	return {
		mode: "light",
		primary: {
			main: primaryMain,
			light: primaryLight,
			dark: primaryDark,
		},
		secondary: {
			main: secondaryColor,
			light: secondaryLight,
			dark: secondaryDark,
		},
		tertiary: {
			main: tertiaryColor,
			light: lighten(tertiaryColor, 12),
			dark: darken(tertiaryColor, 12),
		},
		surfaceTint: {
			main: surfaceTintColor,
			light: lighten(surfaceTintColor, 4),
			dark: darken(surfaceTintColor, 6),
		},
		error: {
			main: "#AB0707",
			light: "#FCD6D0",
			dark: "#AB0707",
		},
		warning: {
			main: "#F8955B",
			light: "#FFE7E0",
			dark: "#F26716",
		},
		surface: {
			main: surfaceMain,
			light: surfaceLight,
			dark: surfaceDark,
			dark2: surfaceColor ? darken(surfaceColor, 30) : "#ADA6B3",
		},
		accent: {
			main: primaryDark,
		},
		icon: {
			main: iconMain,
			light: iconLight,
			dark: "#4C4C56",
			dark2: primaryDark,
		},
		outline: {
			main: outlineMain,
			light: outlineLight,
			dark: outlineDark,
			dark2: "#4C4C56",
		},
		menu: {
			main: primaryDark,
			light: primaryMain,
			light2: lighten(primaryMain, 8),
		},
		textMain: {
			main: "#E5E6E6",
			main2: config.textMainMain2 || "#79797D",
			main3: primaryLight,
			light: "#FFFFFF",
			dark: "#CCCCCE",
			light2: "#FCFCFC",
			dark2: config.textMainDark2 || "#1F2026",
			light3: "#F8F8F8",
			dark3: "#F26716",
			light4: "#F5F5F5",
			dark4: secondaryDark,
		},
		textPrimary: {
			main: "#1F2026",
			light: "#1F2026",
		},
		textSecondary: {
			main: config.textSecondaryMain || "#79797D",
			light: primaryLight,
		},
		textLinkHelpSupport: {
			main: darken(secondaryColor, 10),
		},
	};
}
